import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Car, User, LogOut, Settings } from 'lucide-react';
import MobileNavigation from './MobileNavigation';
import { generateSEOMetadata, updateMetaTags } from '../utils/seo';
import { supabase } from '../lib/supabase';

interface Profile {
  full_name: string;
  avatar_url: string | null;
}

export default function Layout() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);

    const updateSEO = async () => {
      try {
        const metadata = await generateSEOMetadata(location.pathname);
        updateMetaTags(metadata);
      } catch (error) {
        console.error('SEO update error:', error);
      }
    };

    updateSEO();
  }, [location.pathname]);

  useEffect(() => {
    loadProfile();

    // Auth durumu değişikliklerini dinle
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        loadProfile();
      } else {
        setProfile(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setProfile(null);
        return;
      }

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('full_name, avatar_url')
        .eq('id', user.id)
        .single();

      if (profile) {
        setProfile(profile);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/giris');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleMobileNavClick = (path: string) => {
    setIsMenuOpen(false);
    navigate(path);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#1a1d24]">
      <header className="relative z-50">
        {/* Glass Background */}
        <div className="absolute inset-0 backdrop-blur-md bg-[#1a1d24]/80" />

        <div className="relative">
          {/* Main Header */}
          <div className="container mx-auto px-2 sm:px-4">
            <div className="py-4 flex items-center justify-between">
              {/* Logo */}
              <Link to="/" className="flex items-center gap-3 group">
                <div className="relative">
                  <svg
                    width="64"
                    height="64"
                    viewBox="0 0 100 100"
                    xmlns="http://www.w3.org/2000/svg"
                    className="transition-all duration-300 group-hover:scale-105"
                  >
                    <defs>
                      {/* Glow Effect */}
                      <filter id="glow" x="-30%" y="-30%" width="160%" height="160%">
                        <feGaussianBlur in="SourceAlpha" stdDeviation="6" result="blur" />
                        <feOffset dx="0" dy="0" />
                        <feFlood floodColor="#facc15" result="color" />
                        <feComposite in2="blur" operator="in" />
                        <feMerge>
                          <feMergeNode />
                          <feMergeNode in="SourceGraphic" />
                        </feMerge>
                      </filter>

                      {/* Gradient */}
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style={{ stopColor: "#facc15", stopOpacity: 1 }} />
                        <stop offset="100%" style={{ stopColor: "#eab308", stopOpacity: 1 }} />
                      </linearGradient>
                    </defs>

                    {/* Glow Background */}
                    <rect
                      x="8"
                      y="8"
                      width="84"
                      height="84"
                      rx="20"
                      fill="url(#gradient)"
                      filter="url(#glow)"
                      opacity="0.7"
                      className="transition-opacity duration-300 group-hover:opacity-100"
                    />

                    {/* Main Square */}
                    <rect x="12" y="12" width="76" height="76" rx="20" fill="url(#gradient)" />

                    {/* "T" Letter */}
                    <text
                      x="50%"
                      y="68%"
                      fontSize="54"
                      fontWeight="900"
                      textAnchor="middle"
                      fill="#2d2d2d"
                      className="transition-all duration-300 group-hover:fill-gray-900"
                    >
                      T
                    </text>
                  </svg>
                </div>
              </Link>

              {/* Mobile Menu Button */}
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden relative z-50"
              >
                <div className="w-10 h-10 rounded-lg bg-[#24272e] flex items-center justify-center">
                  {isMenuOpen ? (
                    <X className="w-5 h-5 text-white" />
                  ) : (
                    <Menu className="w-5 h-5 text-white" />
                  )}
                </div>
              </button>

              {/* Desktop Navigation */}
              <nav className="hidden md:flex items-center gap-8">
                <Link to="/" className="nav-link group">
                  <span className="relative text-white">
                    Ana Sayfa
                    <span className="absolute inset-x-0 bottom-0 h-0.5 bg-yellow-400 scale-x-0 group-hover:scale-x-100 transition-transform" />
                  </span>
                </Link>
                <Link to="/nasil-calisir" className="nav-link group">
                  <span className="relative text-white">
                    Nasıl Çalışır?
                    <span className="absolute inset-x-0 bottom-0 h-0.5 bg-yellow-400 scale-x-0 group-hover:scale-x-100 transition-transform" />
                  </span>
                </Link>
                <Link to="/hakkimizda" className="nav-link group">
                  <span className="relative text-white">
                    Hakkımızda
                    <span className="absolute inset-x-0 bottom-0 h-0.5 bg-yellow-400 scale-x-0 group-hover:scale-x-100 transition-transform" />
                  </span>
                </Link>
                <Link to="/iletisim" className="nav-link group">
                  <span className="relative text-white">
                    İletişim
                    <span className="absolute inset-x-0 bottom-0 h-0.5 bg-yellow-400 scale-x-0 group-hover:scale-x-100 transition-transform" />
                  </span>
                </Link>
              </nav>

              {/* Desktop Auth/Profile */}
              <div className="hidden md:flex items-center gap-4">
                {profile ? (
                  <div className="relative">
                    <button
                      onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                      className="flex items-center gap-2 px-4 py-2 bg-[#24272e] hover:bg-[#2d3039] text-white rounded-xl transition-colors"
                    >
                      {profile.avatar_url ? (
                        <img
                          src={profile.avatar_url}
                          alt={profile.full_name}
                          className="w-8 h-8 rounded-full"
                        />
                      ) : (
                        <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-black">
                            {profile.full_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <span className="font-medium">{profile.full_name}</span>
                    </button>

                    {isProfileMenuOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-[#24272e] rounded-xl shadow-lg py-2 border border-white/10">
                        <Link
                          to="/profil"
                          className="flex items-center gap-2 px-4 py-2 text-white hover:bg-[#2d3039] transition-colors"
                          onClick={() => setIsProfileMenuOpen(false)}
                        >
                          <Settings className="w-4 h-4" />
                          <span>Profil Ayarları</span>
                        </Link>
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-2 px-4 py-2 text-red-400 hover:bg-[#2d3039] transition-colors"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>Çıkış Yap</span>
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <Link
                      to="/giris"
                      className="px-4 py-2 text-white hover:text-yellow-400 transition-colors"
                    >
                      Giriş Yap
                    </Link>
                    <Link
                      to="/kayit-ol"
                      className="px-6 py-2.5 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-black font-semibold rounded-xl shadow-lg shadow-yellow-400/20 hover:shadow-yellow-400/30 transition-all"
                    >
                      Kayıt Ol
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Mobile Menu */}
          <div className={`
            fixed inset-0 z-40 bg-[#1a1d24]/95 backdrop-blur-lg transform transition-transform duration-300
            ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}
            md:hidden
          `}>
            <div className="h-full flex flex-col">
              <div className="flex-1 overflow-y-auto py-20 px-6">
                <nav className="flex flex-col gap-6">
                  <button 
                    onClick={() => handleMobileNavClick('/')}
                    className="mobile-nav-link text-left"
                  >
                    <span className="text-2xl font-medium text-white">Ana Sayfa</span>
                  </button>
                  <button 
                    onClick={() => handleMobileNavClick('/nasil-calisir')}
                    className="mobile-nav-link text-left"
                  >
                    <span className="text-2xl font-medium text-white">Nasıl Çalışır?</span>
                  </button>
                  <button 
                    onClick={() => handleMobileNavClick('/hakkimizda')}
                    className="mobile-nav-link text-left"
                  >
                    <span className="text-2xl font-medium text-white">Hakkımızda</span>
                  </button>
                  <button 
                    onClick={() => handleMobileNavClick('/iletisim')}
                    className="mobile-nav-link text-left"
                  >
                    <span className="text-2xl font-medium text-white">İletişim</span>
                  </button>
                </nav>
              </div>
              
              <div className="border-t border-white/10 p-6 space-y-4">
                {profile ? (
                  <>
                    <div className="flex items-center gap-3 mb-4">
                      {profile.avatar_url ? (
                        <img
                          src={profile.avatar_url}
                          alt={profile.full_name}
                          className="w-10 h-10 rounded-full"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center">
                          <span className="text-lg font-medium text-black">
                            {profile.full_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="text-white font-medium">{profile.full_name}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        navigate('/profil');
                      }}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#24272e] hover:bg-[#2d3039] text-white rounded-xl transition-colors"
                    >
                      <Settings className="w-5 h-5" />
                      <span>Profil Ayarları</span>
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-xl transition-colors"
                    >
                      <LogOut className="w-5 h-5" />
                      <span>Çıkış Yap</span>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => navigate('/giris')}
                      className="w-full py-3 text-white text-lg font-medium hover:text-yellow-400 transition-colors"
                    >
                      Giriş Yap
                    </button>
                    <button
                      onClick={() => navigate('/kayit-ol')}
                      className="w-full py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-black text-lg font-semibold rounded-xl shadow-lg"
                    >
                      Kayıt Ol
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 container mx-auto px-2 sm:px-4 py-8 pb-24 md:pb-8">
        <Outlet />
      </main>

      {/* Mobile Navigation */}
      <MobileNavigation hideNav={isMenuOpen} />

      {/* Minimalist Footer */}
      <footer className="bg-[#24272e]/50 backdrop-blur-sm mt-auto border-t border-white/10">
        <div className="container mx-auto px-4 py-6 md:py-8">
          {/* Logo & Links - Mobile Optimized */}
          <div className="flex flex-col items-center space-y-6">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-lg flex items-center justify-center">
                <span className="text-xl font-black text-black">T</span>
              </div>
              <span className="text-white font-semibold">En Yakın Taksi</span>
            </Link>

            {/* Quick Links - Mobile Grid */}
            <div className="grid grid-cols-2 gap-4 w-full max-w-xs text-center">
              <Link to="/nasil-calisir" className="text-gray-400 hover:text-yellow-400 transition-colors text-sm">
                Nasıl Çalışır?
              </Link>
              <Link to="/hakkimizda" className="text-gray-400 hover:text-yellow-400 transition-colors text-sm">
                Hakkımızda
              </Link>
              <Link to="/gizlilik" className="text-gray-400 hover:text-yellow-400 transition-colors text-sm">
                Gizlilik
              </Link>
              <Link to="/kullanici-sozlesmesi" className="text-gray-400 hover:text-yellow-400 transition-colors text-sm">
                Kullanım Şartları
              </Link>
            </div>

            {/* Contact - Compact */}
            <div className="flex items-center gap-4">
              <a 
                href="mailto:info@enyakintaksi.app" 
                className="text-gray-400 hover:text-yellow-400 transition-colors text-sm"
              >
                info@enyakintaksi.app
              </a>
            </div>

            {/* Copyright - Minimal */}
            <div className="text-center text-sm text-gray-500">
              &copy; {new Date().getFullYear()}  
              <a 
                href="https://enyakintaksi.app" 
                className="text-yellow-400 hover:underline" 
                target="_blank" 
                rel="noopener noreferrer"
              >  En Yakın Taksi
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}